import { useAppSelector, useAppDispatch } from "@/app/hooks";
import { logout } from "@/features/auth/authSlice";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { queryClient } from "@/lib/queryClient";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Search, Menu, Moon, Sun, LogOut } from "lucide-react";

interface TopbarProps {
  onMenuClick: () => void;
}

export function Topbar({ onMenuClick }: TopbarProps) {
  const user = useAppSelector((state) => state.auth.user);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [dark, setDark] = useState(() =>
    document.documentElement.classList.contains("dark"),
  );
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (dark) document.documentElement.classList.add("dark");
    else document.documentElement.classList.remove("dark");
  }, [dark]);

  const handleLogout = () => {
    dispatch(logout());
    queryClient.clear();
    navigate("/login");
  };

  const getDesignationFromStorage = () => {
    try {
      const storedAuth = localStorage.getItem("auth_state");
      if (storedAuth) {
        const parsed = JSON.parse(storedAuth);
        return parsed.user?.designation || null;
      }
    } catch (error) {
      console.error("Error reading from localStorage:", error);
    }
    return null;
  };

  const designation = getDesignationFromStorage();

  const ROLE_LABELS: Record<string, string> = {
    super_admin: "Super Admin",
    admin: "Admin",
    manager: "Manager",
    team_leader: "Team Leader",
    agent: "Agent",
    telecaller: "Telecaller",
  };

  // const roleBadge = user?.role ? (ROLE_LABELS[user.role] ?? "User") : "User";
  const getDisplayRole = () => {
    if (designation?.toLowerCase() === "telecaller") {
      return "Telecaller";
    }
    return user?.role ? (ROLE_LABELS[user.role] ?? "User") : "User";
  };

  const roleBadge = getDisplayRole();

  return (
    <header className="sticky top-0 z-30 flex min-h-14 items-center gap-3 border-b border-border bg-card px-3 sm:px-4">
      <button
        onClick={onMenuClick}
        className="rounded-md p-1.5 text-muted-foreground hover:bg-muted md:hidden"
      >
        <Menu className="h-5 w-5" />
      </button>

      <div className="flex min-w-0 flex-1 items-center gap-2">
        <div className="relative hidden max-w-md flex-1 sm:block">
          {/* <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search leads, cases, payments..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-9 w-full rounded-md border border-input bg-background pl-9 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          /> */}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-1 sm:gap-2">
        <button
          onClick={() => setDark(!dark)}
          className="rounded-md p-1.5 text-muted-foreground hover:bg-muted"
          title="Toggle theme"
        >
          {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </button>

        <NotificationBell />

        <div className="flex min-w-0 cursor-pointer items-center gap-2 rounded-md px-1 py-1 hover:bg-muted sm:px-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
            {user?.name
              ?.split(" ")
              .map((n) => n[0])
              .join("") || "U"}
          </div>
          <div className="hidden sm:block">
            <p className="text-sm font-medium leading-none text-foreground">
              {user?.name}
            </p>
            <p className="text-[11px] text-muted-foreground">{roleBadge}</p>
          </div>
        </div>

        <button
          onClick={handleLogout}
          className="rounded-md p-1.5 text-muted-foreground hover:bg-muted"
          title="Logout"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </header>
  );
}
